import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, DownloadCloud, RefreshCw, TriangleAlert, XCircle } from 'lucide-react';
import type {
  AppUpdateCheckDto,
  AppUpdatePushResult,
  AppUpdatePushRow,
  PortalReloadResult,
  PortalReloadRow,
} from '@gc/shared';

import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/features/auth/useAuth';
import { Section } from './controls';

/**
 * "Update the gaming PCs now."
 *
 * Every machine already checks for a published release every half hour and
 * installs it at its next logon. That is the right default — nobody's game is
 * interrupted by a release — but it is a poor experience for the person who has
 * just published a fix and wants to see it land. This is the button for that.
 *
 * **It hurries the machines; it does not push code at them.** The frame the
 * server sends carries no URL, no version and no bytes: each PC goes to the
 * server it was linked to and refuses anything whose checksum does not match
 * what that server declared. The worst a forged nudge could do is make a PC
 * update slightly earlier than it already would have.
 *
 * A machine somebody is playing on is told to DOWNLOAD only, and swaps at its
 * next logon. That distinction is the whole reason the outcome list exists.
 */
export function KioskUpdateSection({ id }: { id?: string }) {
  const { t } = useTranslation();
  const { can } = useAuth();

  const [published, setPublished] = useState<string | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState<AppUpdatePushRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The refresh keeps its own state rather than sharing the update's: the two
  // answer different questions, and a refresh result landing in the update's
  // list would read as "the app updated" over machines nothing was installed on.
  const [refreshBusy, setRefreshBusy] = useState(false);
  const [refreshRows, setRefreshRows] = useState<PortalReloadRow[] | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);

  useEffect(() => {
    const abort = new AbortController();
    api
      .get<AppUpdateCheckDto>('/app-releases/current', abort.signal)
      .then((result) => setPublished(result.version))
      // Silent: an older API or a deployment that never publishes must leave
      // this section quiet rather than shouting about a feature nobody uses.
      .catch(() => setPublished(null));
    return () => abort.abort();
  }, []);

  // Nothing published means nothing to send. Saying so beats a button that
  // cheerfully reports "sent to 28 devices" and changes nothing on any of them.
  if (!can('devices.manage') || published === undefined) return null;

  async function push(): Promise<void> {
    setBusy(true);
    setError(null);
    setRows(null);
    try {
      const result = await api.post<AppUpdatePushResult>('/devices/update-app', {});
      setRows(result.rows);
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : t('settings.general.kioskUpdateFailed'),
      );
    } finally {
      setBusy(false);
    }
  }

  async function refresh(): Promise<void> {
    setRefreshBusy(true);
    setRefreshError(null);
    setRefreshRows(null);
    try {
      const result = await api.post<PortalReloadResult>('/devices/reload-portal', {});
      setRefreshRows(result.rows);
    } catch (caught) {
      setRefreshError(
        caught instanceof ApiError ? caught.message : t('settings.general.portalRefreshFailed'),
      );
    } finally {
      setRefreshBusy(false);
    }
  }

  const counts = {
    sent: rows?.filter((row) => row.outcome === 'sent').length ?? 0,
    staged: rows?.filter((row) => row.outcome === 'staged').length ?? 0,
  };

  const refreshCounts = {
    sent: refreshRows?.filter((row) => row.outcome === 'sent').length ?? 0,
    skipped: refreshRows?.filter((row) => row.outcome === 'skipped_in_session').length ?? 0,
  };

  return (
    <Section id={id} title={t('settings.general.kioskSection')}>
      <p className="text-sm text-content-secondary">
        {published
          ? t('settings.general.kioskUpdateHint', { version: published })
          : t('settings.general.kioskNothingPublished')}
      </p>

      <button
        // NEVER `submit`: this sits inside the settings form, and pressing it
        // must not also save every unrelated field on the page.
        type="button"
        data-push-app-update
        disabled={busy || !published}
        onClick={() => void push()}
        className="mt-3 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-fg transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
      >
        <DownloadCloud className="h-4 w-4" aria-hidden />
        {busy ? t('settings.general.kioskUpdateSending') : t('settings.general.kioskUpdateNow')}
      </button>

      {error ? (
        <p role="alert" className="mt-3 text-sm text-danger">
          {error}
        </p>
      ) : null}

      {rows ? (
        <>
          <p className="mt-4 text-sm text-content">
            {t('settings.general.kioskUpdateSummary', counts)}
          </p>
          <ul className="mt-2 space-y-1">
            {rows.map((row) => (
              <li
                key={row.deviceId}
                data-update-outcome={row.outcome}
                className="flex items-start gap-2 rounded-control bg-input px-2 py-1.5 text-xs"
              >
                {row.outcome === 'sent' ? (
                  <CheckCircle2
                    className="mt-0.5 h-3.5 w-3.5 shrink-0 text-available"
                    aria-hidden
                  />
                ) : row.outcome === 'no_agent' ? (
                  <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-content-muted" aria-hidden />
                ) : (
                  <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ending" aria-hidden />
                )}
                <span className="min-w-0">
                  <span className="font-medium text-content">{row.deviceName}</span>
                  <span className="block text-content-muted">
                    {t(`settings.general.kioskOutcome.${row.outcome}`)}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {/* ---------------------------------------------------------------- */}
      {/* The OTHER button, and the difference is the whole point of the    */}
      {/* copy: updating replaces the app, refreshing re-fetches the page.  */}
      {/* ---------------------------------------------------------------- */}
      <div className="mt-6 border-t border-subtle pt-4">
        <p className="text-sm text-content-secondary">{t('settings.general.portalRefreshHint')}</p>

        <button
          type="button"
          data-push-portal-reload
          disabled={refreshBusy}
          onClick={() => void refresh()}
          // Outlined, not filled: this section already has one primary action,
          // and two glowing buttons side by side would make the operator pick
          // by reading rather than by recognising.
          className="mt-3 inline-flex items-center gap-2 rounded-full border border-strong px-5 py-2.5 text-sm font-semibold text-content transition-colors hover:bg-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          <RefreshCw className="h-4 w-4" aria-hidden />
          {refreshBusy
            ? t('settings.general.portalRefreshSending')
            : t('settings.general.portalRefreshNow')}
        </button>

        {refreshError ? (
          <p role="alert" className="mt-3 text-sm text-danger">
            {refreshError}
          </p>
        ) : null}

        {refreshRows ? (
          <>
            <p className="mt-4 text-sm text-content">
              {t('settings.general.portalRefreshSummary', refreshCounts)}
            </p>
            <ul className="mt-2 space-y-1">
              {refreshRows.map((row) => (
                <li
                  key={row.deviceId}
                  data-reload-outcome={row.outcome}
                  className="flex items-start gap-2 rounded-control bg-input px-2 py-1.5 text-xs"
                >
                  {row.outcome === 'sent' ? (
                    <CheckCircle2
                      className="mt-0.5 h-3.5 w-3.5 shrink-0 text-available"
                      aria-hidden
                    />
                  ) : row.outcome === 'no_agent' ? (
                    <XCircle
                      className="mt-0.5 h-3.5 w-3.5 shrink-0 text-content-muted"
                      aria-hidden
                    />
                  ) : (
                    <TriangleAlert
                      className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ending"
                      aria-hidden
                    />
                  )}
                  <span className="min-w-0">
                    <span className="font-medium text-content">{row.deviceName}</span>
                    <span className="block text-content-muted">
                      {t(`settings.general.portalReloadOutcome.${row.outcome}`)}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </div>
    </Section>
  );
}
